import { mpan_search_limit } from "../config/constants";
import {
  AdditionalMeterDetail,
  Address,
  AddressSearchResponse,
  MeterDetails,
} from "../types/components.types";
import {
  MeterSearchResult,
  MeterDataResponse,
  DecryptedMpanResponse,
  AddressSearchApiResponse,
  TaqApiResponse,
  TaqResponse,
  MultiMpanResult,
} from "../types/services.types";
import { isHalfHourlyMeter } from "../utils/electralink/electralink.utils";

import { apiGet, getApiErrorMessage, isHttpError } from "./apiService";
import { electralinkEndpoints as endpoints } from "./electralink.endpoints";

// Fetch TAQ data for half-hourly meters.
export const getAqCalc = async (mpan: string): Promise<TaqResponse> => {
  // Return early if for whatever reason the mpan was not provided.
  if (!mpan) {
    console.log("MPAN is required to fetch TAQ data");
    return { success: false, error: "MPAN is required" };
  }
  try {
    // Call the hh_aq_calc endpoint, to fetch the TAQ calculation for the MPAN.
    const data = await apiGet<TaqApiResponse>(`${endpoints.hh_aq_calc}/${mpan}`);
    console.log("TAQ calculation retrieved", data.taq);
    return { success: true, aqCalc: data };
  } catch (error) {
    // Log the thrown error from the network.
    console.log("Error fetching TAQ data:", error);
    return { success: false, error: "Failed to fetch TAQ data." };
  }
};

// Encapsulate the logic for building the meterDetails object in one place (optionally including the TAQ field), to avoid repeating the same object for other endpoints.
const createMeterDetails = async (data: MeterDataResponse): Promise<MeterDetails> => {
  const meterDetails: MeterDetails = {
    additional_detail: data.additional_detail,
    et: data.et,
    metering_point_address_line_1: data.metering_point_address_line_1,
    metering_point_address_line_2: data.metering_point_address_line_2,
    metering_point_address_line_3: data.metering_point_address_line_3,
    metering_point_address_line_4: data.metering_point_address_line_4,
    metering_point_address_line_5: data.metering_point_address_line_5,
    metering_point_address_line_6: data.metering_point_address_line_6,
    metering_point_address_line_7: data.metering_point_address_line_7,
    metering_point_address_line_8: data.metering_point_address_line_8,
    metering_point_address_line_9: data.metering_point_address_line_9,
    mpan: data.mpan,
    mpan_top_line: data.mpan_top_line,
    post_code: data.post_code,
    supplier_efd: data.supplier_efd,
    supplier_id: data.supplier_id,
    supplier_name: data.supplier_name,
    total_eac: data.total_eac,
    tpr_data: data.tpr_data,
    capacity: data.capacity,
    disconnected_mpan: data.disconnected_mpan,
    disconnection_date: data.disconnection_date,
    energisation_status: data.energisation_status,
    energisation_status_effective_from_date: data.energisation_status_effective_from_date,
    line_loss_factor_class_id: data.line_loss_factor_class_id,
    line_loss_factor_class_id_effective_from_date:
      data.line_loss_factor_class_id_effective_from_date,
    standard_settlement_configuration_id: data.standard_settlement_configuration_id,
    standard_settlement_configuration_id_effective_from_date:
      data.standard_settlement_configuration_id_effective_from_date,
  };
  console.log("createMeterDetails object is built:", data);

  // Check if the meter is half hourly to call the TAQ endpoint.
  if (isHalfHourlyMeter(data.additional_detail)) {
    console.log('This meter"s profile is half hourly. Fetching its TAQ calculation...');

    // Mark the meter as half-hourly, so we can render the UI for half hourly meters.
    meterDetails.is_half_hourly = true;

    try {
      // Fetch TAQ calculation for the MPAN.
      const taqResponse = await getAqCalc(data.mpan);

      // If TAQ was successfully retrieved, add it to meter details.
      if (taqResponse.success && taqResponse.aqCalc) {
        meterDetails.hh_aq_calc = taqResponse.aqCalc;
      } else {
        // If TAQ fetch failed, log and set null to avoid breaking the flow and still return meter details but without TAQ.
        console.log("Failed to fetch TAQ data, but continuing with fetching meter details");
        meterDetails.hh_aq_calc = null;
      }
    } catch (error) {
      // Handle and log errors.
      console.log("Error fetching TAQ data:", error);
      meterDetails.hh_aq_calc = null;
    }
  }
  return meterDetails;
};

const padAndSlice = (
  value: string,
  padBy: number,
  sliceBy: number,
  padWith = "0",
  sliceFromRight = true,
) => {
  if (value === "") return "";

  const padded = value.padStart(padBy, padWith);
  return sliceFromRight ? padded.slice(-sliceBy) : padded.slice(0, sliceBy);
};

const createMpanTopLine = (data: MeterDataResponse): string => {
  const profileClass = data.additional_detail[0]?.additional_detail_profile_class;
  const ssc = data.standard_settlement_configuration_id;
  const llfc = data.line_loss_factor_class_id;

  if (!profileClass || !ssc || !llfc) {
    console.log("Missing data for MPAN top line construction:", { profileClass, ssc, llfc });
    return "";
  }

  // Use the last 3 characters of the LLFC to match Joe's Zoho implementation (llfc.right(3)).
  const profileClassPadded = padAndSlice(profileClass, 2, 2);
  const sscPadded = padAndSlice(ssc, 4, 4);
  const llfcPadded = padAndSlice(llfc, 3, 3);

  return `${profileClassPadded}${sscPadded}${llfcPadded}`;
};

// Mpan Tab Search
export const searchMeterByMpan = async (mpan: MeterDetails["mpan"]): Promise<MeterSearchResult> => {
  // Mpan is exactly 13 digits.
  const mpanRegex = /^[0-9]{13}$/;

  // Remove any whitespaces from the MPAN input.
  const cleanMpan = mpan.replace(/\s/g, "");

  // Test the cleanMpan against the regex.
  const isValid = mpanRegex.test(cleanMpan);

  // Return early if the Mpan is invalid.
  if (!isValid) {
    console.log("Invalid MPAN provided");
    return { isValid, meterDetails: null };
  }

  try {
    // If the Mpan is valid, make a GET request to the API endpoint, passing the clean MPAN.
    const data = await apiGet<MeterDataResponse>(`${endpoints.decrypted_mpan_search}/${cleanMpan}`);
    const mpanTopLine = createMpanTopLine(data);
    data.mpan_top_line = mpanTopLine;
    // Check if the API request is successful and contains the required data.
    if (data.status_code === 200) {
      // Create meter details with conditional TAQ data.
      const meterDetails = await createMeterDetails(data);
      return { isValid: true, meterDetails };

      // Throw an error if no data was returned.
    } else {
      throw new Error("No data returned from the API.");
    }
  } catch (error) {
    console.log("Error fetching meter details:", error);
    if (isHttpError(error)) {
      return {
        isValid: true,
        meterDetails: null,
        error: getApiErrorMessage(error, "No meter was found for this MPAN."),
      };
    }
    throw error;
  }
};

// MSN Tab Search
export const searchMeterByMsn = async (
  msn: AdditionalMeterDetail["additional_detail_msn"],
  postcode: MeterDetails["post_code"],
): Promise<MeterSearchResult> => {
  // MSN must be 8-13 alphanumeric characters.
  const msnRegex = /^[A-Za-z0-9]{8,13}$/;

  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

  // Remove any whitespaces from the inputs
  const cleanMsn = msn.replace(/\s/g, "");
  const cleanPostcode = postcode.replace(/\s/g, "");

  // Test the cleanMsn against the regex.
  const isValid = msnRegex.test(cleanMsn);

  // Return early if the MSN is invalid.
  if (!isValid) {
    console.log("Invalid MSN provided");
    return { isValid: false, meterDetails: null };
  }

  // Validate postcode format
  if (!postcodeRegex.test(cleanPostcode)) {
    console.log("Invalid postcode format.");
    return { isValid: false, meterDetails: null };
  }

  try {
    // If the MSN and postcode inputs are valid, make a GET request to the API endpoint, passing the clean MSN and postcode.
    // The response should match the MeterDataResponse TS Type.
    const data = await apiGet<MeterDataResponse>(
      `${endpoints.msn_search}/${cleanMsn}/${cleanPostcode}`,
    );
    const mpanTopLine = createMpanTopLine(data);
    data.mpan_top_line = mpanTopLine;

    // Check if the API request is successful and contains the required data
    if (data.status_code === 200) {
      // Create meter details with conditional TAQ data.
      const meterDetails = await createMeterDetails(data);

      return { isValid: true, meterDetails };

      // Throw an error if no data was returned.
    } else {
      throw new Error("No data received from the API.");
    }
  } catch (error) {
    console.log("Error fetching meter details by MSN:", error);
    if (isHttpError(error)) {
      return {
        isValid: true,
        meterDetails: null,
        error: getApiErrorMessage(error, "No meter was found for this MSN and postcode."),
      };
    }
    throw error;
  }
};

// Postcode Tab Search
export const searchAddressByPostcode = async (
  postcode: MeterDetails["post_code"],
): Promise<AddressSearchResponse> => {
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
  const isValid = postcodeRegex.test(postcode);
  if (!isValid) {
    return { success: false, message: "Invalid postcode format." };
  }
  try {
    const data = await apiGet<AddressSearchApiResponse>(`${endpoints.postcode_search}/${postcode}`);
    if (data.status_code === 200) {
      const addresses: Address[] = data.json.map((item) => ({
        address: item.address,
        mpan: item.mpan,
      }));
      return {
        success: true,
        addresses,
      };
    } else {
      console.log("Invalid data format received from the middleware");
      throw new Error("Invalid data format received from the middleware");
    }
  } catch (error) {
    console.log("error", error);
    if (isHttpError(error)) {
      const statusCode = error.response.status;
      if (statusCode === 404) {
        return {
          success: false,
          message: "The postcode you entered is incorrect. Please try a different one.",
        };
      } else if (statusCode === 500 || statusCode === 502) {
        return { success: false, message: "Server is down. Please try again later." };
      } else {
        return {
          success: false,
          message: `Server error (${String(statusCode)}). Please try again.`,
        };
      }
    }
    throw error;
  }
};

export const getDecryptedMpan = async (encryptedMpan: string): Promise<DecryptedMpanResponse> => {
  if (!encryptedMpan) {
    console.log("Encrypted MPAN is required");
    throw new Error("Encrypted MPAN is required");
  }
  try {
    const data = await apiGet<MeterDataResponse>(
      `${endpoints.encrypted_mpan_search}/${encryptedMpan}`,
    );
    if (data.status_code === 200) {
      console.log("/encmpan data:", data);
      return {
        success: true,
        decryptedData: data,
      };
    } else {
      console.log("Invalid data format received from the middleware.");
      throw new Error("Failed to retrieve the decrypted Mpan.");
    }
  } catch (error) {
    console.log("Error fetching the decrypted Mpan:", error);
    throw error;
  }
};

// Fetch meter details from a postcode search.
export const getMeterDetailsByAddress = async (address: Address): Promise<MeterDetails> => {
  try {
    const encryptedMpan = address.mpan;

    // Get the decrypted Mpan.
    const decryptedResponse = await getDecryptedMpan(encryptedMpan);
    if (decryptedResponse.success) {
      // Extract the decrypted MPAN and other meter details.
      const decryptedData = decryptedResponse.decryptedData;
      const mpanTopLine = createMpanTopLine(decryptedData);
      decryptedData.mpan_top_line = mpanTopLine;

      // Create meter details with conditional TAQ data.
      const meterDetails = await createMeterDetails(decryptedData);

      return meterDetails;
    } else {
      console.log("Failed to decrypt the MPAN.");
      throw new Error("Failed to decrypt MPAN");
    }
  } catch (error) {
    console.log("Error getting meter details by address:", error);
    throw error;
  }
};

// Search for multiple MPANs in parallel. The backend doesn't provide a bulk search endpoint, so we need to make individual requests.
export const searchMultipleMetersByMpan = async (mpans: string[]): Promise<MultiMpanResult[]> => {
  // Implement a maximum limit to prevent excessive API calls.
  if (mpans.length > mpan_search_limit) {
    throw new Error(`Maximum ${String(mpan_search_limit)} MPANs allowed per search`);
  }

  // Map each MPAN to a promise. Since there's no bulk endpoint, we need to fire off individual requests in parallel to reduce the total wait time.
  const promises = mpans.map(async (mpan) => {
    try {
      // Reuse the existing single-MPAN search function to avoid duplicating validation and API logic.
      const result = await searchMeterByMpan(mpan);
      if (!result.meterDetails) {
        return {
          mpan,
          success: false,
          meterDetails: null,
          error: result.error ?? "Failed to retrieve meter details",
        };
      }
      return {
        mpan,
        success: true,
        meterDetails: result.meterDetails,
      };
    } catch (error) {
      // Catch any thrown errors (e.g. network failures) and return a failed result. This ensures one failed MPAN doesn't break the entire batch -- other results are still returned.
      return {
        mpan,
        success: false,
        meterDetails: null,
        error: "Failed to retrieve meter details",
      };
    }
  });

  // Wait for all promises to resolve.
  // Every MPAN will return a result (success or failure).
  return Promise.all(promises);
};
